import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import clone from 'lodash/clone';
import findIndex from 'lodash/findIndex';
import { rootNode, TREE_TYPE_DELIMETER, TREE_KEY_DELIMETER, PREFIX_OBJ_KEY } from '@/constants/tree';
import { menuDepthDefine, menuDefine } from '@/constants/menu';
import { getPathNodes, getPathNodeDatas } from '@/utils/tree';
import { startRoutePathHistHandlerTimer, clearRoutePathHistHandlerTimer, getTreeTypeFromTreeKey, getTreeKeyExceptTreeType } from '@/utils/router';

const DEFAULT_TREE_REF = 'routerTree';
/**
 * 라우트 트리 믹스인
 * @author Ju
 */
export default {
  data() {
    return {
      /**
       * 트리 컴포넌트 reference
       * @type {String}
       */
      treeRef: DEFAULT_TREE_REF,
      /**
       * 트리 타입 (required)
       * 해당 트리 타입은 반드시 @/consatns/tree의 treeType에서 상수로 정의하여 관리하도록 한다.
       * @type {String}
       */
      treeType: null,
      /**
       * 트리 데이터
       * @type {Object}, {Array}
       */
      treeData: null,
      /**
       * 동일 메뉴에 있는 트리 타입 배열
       * 동일 메뉴에 트리가 여러 개인 경우 반드시 설정해한다.
       * 첫 번째 트리 타입이 default로 redirect 시 우선 순위를 가지므로 이를 고려하여 배열 설정 필요하다.
       * @type {Array}
       * (ex) [treeType.LOGICAL, treeType: PHYSICAL]
       */
      treeTypesInMenu: null,
      /**
       * 동적 트리 여부
       * @type {Boolean}
       */
      dynamicFlag: false,
      /**
       * 트리 데이터 로드 중인지 여부
       * @type {Boolean}
       */
      loading: false,
      /* Dynamic */
      /**
       * 라우트할 트리키 정보
       * @type {String}
       */
      routeTreeKey: null,
      /**
       * 라우트할 트리키 기준으로 트리 데이터 로드했는지 여부
       * @type {Boolean}
       */
      loadedRouteNode: false,
      /**
       * 최초 로드 여부
       * @type {Boolean}
       */
      firstLoaded: false,
      /**
       * el-scroll reference
       * @type {String}
       */
      scrollRef: 'scroll',
      /**
       * 현재 스크롤 위치 고정 여부
       * selectedNod가 설정되는 시점에 해당 값이 true이면 선택된 노드 위치로
       * 스크롤을 이동하며, 그렇지 않을 경우 현재 스크롤 위치를 고정한다.
       * 사용자가 직접 트리 노드를 클릭한 경우는 스크롤 위치가 고정되어야 하므로 node-click 이벤트 핸들러에서 rotueTreeKey 호출 전 true로 설정한다.
       * @type {Boolean}
       */
      fixScroll: false,
      /**
       * 스크롤 위치 이동 Task
       * @type {String}
       */
      scrollTask: 'routeTreeScrollTask',
      /**
       * 라우트 경로 이력 핸들러 타이머
       * @type {Number}
       */
      routePathHistHandlerTimer: null,
    };
  },
  computed: {
    /**
     * 선택된 메뉴
     * @author Ju
     * @return {String} 선택된 메뉴의 아이디
     */
    selectedMenu() {
      return this.$store.getters.selectedMenu;
    },
    /**
     * 선택된 트리 노드 데이터 객체
     * @author Ju
     * @return {Object}
     */
    selectedNode() {
      return this.$store.getters.selectedNode;
    },
    rootMenuInfo() {
      return this.$store.getters.rootMenuInfo;
    },
    /**
     * 링크를 통해 라우트가 전환 됐는지 여부.
     * @author Ju
     * @return {Boolean} [description]
     */
    isLinkRoute() {
      return this.$store.state.router.isLinkRoute;
    },
  },
  watch: {
    $route: {
      deep: true,
      immediate: true,
      handler(to, from) {
        if (this.dynamicFlag || !isEmpty(this.treeData)) {
          if (isEmpty(from)) {
            this.$nextTick(() => {
              this.changeRouteTreeKey(to.params.treeKey);
            });
          } else {
            const toMenu = to.path.split('/')[1];
            const fromMenu = isEmpty(from) ? null : from.path.split('/')[1];

            if (toMenu !== fromMenu || to.params.treeKey !== from.params.treeKey) {
              this.$nextTick(() => {
                this.changeRouteTreeKey(to.params.treeKey);
              });
            }
          }
        }
      },
    },
    treeData: {
      immediate: true,
      handler(to) {
        this.handlerForChangeTreeDataBeforeRoute(to);
        if (!isEmpty(to)) {
          this.$nextTick(() => {
            this.changeRouteTreeKey(this.$route.params.treeKey);
          });
        }
      },
    },
  },
  beforeDestroy() {
    this.$taskManager.removeTask(this.scrollTask);
  },
  methods: {
    /**
     * 라우트 트리키 변경 사항을 처리한다.
     * @author Ju
     * @param  {String} routeTreeKey 라우트 트리키
     */
    changeRouteTreeKey(routeTreeKey) {
      console.debug(`[${this.treeType} change route tree key]`, routeTreeKey, this.loading, this.loadedRouteNode, new Date());
      const treeType = getTreeTypeFromTreeKey(routeTreeKey);
      const treeKey = getTreeKeyExceptTreeType(routeTreeKey);
      if (isNil(treeType)) {
        // 트리키에 트리타입이 없는 경우 트리타입을 포함하는 트리키로 redirect
        if (this.isDefaultTreeType()) {
          const node = this.$refs[this.treeRef].getNode(treeKey);
          if (!isEmpty(node) && this.isValidNode(node)) {
            this.routeByTreeKey(this.concatTreeTypeAndTreeKey(treeKey), true);
          } else if (!this.dynamicFlag && !isEmpty(this.treeData)) {
            this.routeByTreeKey(this.getDefaultRouteTreeKey(), true);
          } else if (this.dynamicFlag) {
            // 19.09.19 Ju 토폴로지맵에서 트리 타입 처리하고 나면 주석의 소스로 변경
            this.routeByTreeKey(this.getDefaultRouteTreeKey(), true);
            // this.loadRouteTreeKeyNode(this.concatTreeTypeAndTreeKey(treeKey));
          }
        } else {
          this.clearCurrnetNode();
        }
      } else if ((isEmpty(this.treeTypesInMenu) && this.treeType !== treeType) || (!isEmpty(this.treeTypesInMenu) && this.treeTypesInMenu.indexOf(treeType) < 0 && this.treeType === this.treeTypesInMenu[0])) {
        // 트리 타압이 다른 경우 다른 트리키에 대한 정보이므로 root로 redirect
        this.routeByTreeKey(this.getDefaultRouteTreeKey(), true);
      } else if (this.treeType === treeType) {
        const node = this.$refs[this.treeRef].getNode(treeKey);
        if (!isEmpty(node) && this.isValidNode(node)) {
          if (this.dynamicFlag) {
            this.loadedRouteNode = false;
          }
          this.setSelectedNode(node);
        } else if (this.dynamicFlag) {
          if (this.loadedRouteNode) {
            this.loadedRouteNode = false;
            this.routeByTreeKey(this.getDefaultRouteTreeKey(), true);
          } else {
            this.loadRouteTreeKeyNode(routeTreeKey);
          }
        } else if (!isEmpty(this.treeData)) {
          this.routeByTreeKey(this.getDefaultRouteTreeKey(), true);
        }
      } else {
        this.clearCurrnetNode();
      }
    },
    /**
     * 여러 트리를 제공할 때 해당 트리가 기본 트리인지 확인한다.
     * @author Ju
     * @return {Boolean} [description]
     */
    isDefaultTreeType() {
      return isEmpty(this.treeTypesInMenu) || this.treeType === this.treeTypesInMenu[0];
    },
    /**
     * 현재 선택된 노드를 해제한다.
     * @author Ju
     * @return {[type]} [description]
     */
    clearCurrnetNode() {
      if (this.$refs[this.treeRef] && !isEmpty(this.$refs[this.treeRef].getCurrentNode())) {
        this.$refs[this.treeRef].setCurrentKey(null);
      }
    },
    /**
     * 변경된 라우트 트리키에 맞게 선택된 노드를 변경하고 store에 관련 상태 값을 변경한다.
     * @author Ju
     * @param  {Object} node el-tree 노드 객체
     */
    setSelectedNode(node) {
      const nodeData = clone(node.data);
      const nodePath = clone(getPathNodeDatas(node));
      if (nodeData.type === 'obj' && node.parent.data.type === 'group') {
        nodeData.parent = node.parent.data;
      }
      this.$refs[this.treeRef].setCurrentKey(nodeData.treeKey);
      const path = getPathNodes(node);
      path.forEach((item) => {
        if (!item.isLeaf) {
          item.expanded = true;
        }
      });
      if (this.dynamicFlag && !node.isLeaf && !node.loaded) {
        node.expand();
      }
      // 이전 메뉴 트리에서 선택된 selectedNode로 화면이 제어되지 않도록 하기 위해 추가
      // 간헐적으로 selectedMenu 설정 전 해당 부분 호출되는 경우가 있어 이 경우 path에서 메뉴 정볼르 가져오도록 함.
      let menuUrl;
      let menuDepth;
      if (isEmpty(this.selectedMenu)) {
        menuUrl = this.$route.path.split('/')[1];
        menuDepth = menuDepthDefine[menuUrl];
      } else {
        menuUrl = this.selectedMenu.url;
        menuDepth = this.selectedMenu.menuDepth;
      }
      this.$store.dispatch('setTreeMenu', menuUrl);
      this.$store.dispatch('setTreeType', this.treeType);
      this.$store.dispatch('setSelectedNode', nodeData);
      this.$store.dispatch('setSelectedNodePath', nodePath);

      // routeByTreeKey가 아닌 직접 router.push를 한 경우 2depth 미만의 경우 activeTab이 삭제되지 않으므로 강제로 삭제시켜줌
      if (menuDepth < 3) {
        if (!isEmpty(this.$store.getters.activeTab)) {
          this.$store.dispatch('setActiveTab', null);
          this.$store.dispatch('setTmpActiveTab', null);

          clearRoutePathHistHandlerTimer(this.routePathHistHandlerTimer);
          this.routePathHistHandlerTimer = startRoutePathHistHandlerTimer(this, this.$route.path);
        }
      } else {
        const activeTab = this.$route.params.activeTab;
        this.$nextTick(() => {
          // 새로운 탭 view가 생성되기 전에 호출될 때 그 이전 탭 view의 default tab으로 전환되어 설정되지 않도록 하기 위해 tmpActvietAb 추가
          // 새로운 탭 view의 mounted 시점에 tmpActiveTab이 있을 경우 해당 탭으로 이동시킨다.
          this.$store.dispatch('setTmpActiveTab', activeTab);
        });
      }

      if (!this.fixScroll) {
        this.$nextTick(() => {
          if (!isNil(this.$refs[this.scrollRef])) {
            const currentNodeEl = this.$refs[this.treeRef].$el.querySelector('.el-tree-node.is-current');
            // TODO: Ju 이중 nextTick 구조 개선 필요.
            // 1번째 nextTick에서는 currentNode의 el은 알아올 수 있으나 position 위치 계산이 끝나지 않기 때문에
            // 2번째 nextTick에서 offsetTop 계산하여 스크롤 위치 이동 시킴
            if (!isNil(currentNodeEl)) {
              this.$nextTick(() => {
                this.createScrollTask(currentNodeEl);
                this.$taskManager.startTask(this.scrollTask);
              });
            }
          }
        });
      } else {
        this.fixScroll = false;
      }
    },
    /**
     * 트리 노드의 키로 라우팅한다.
     * @author Ju
     * @param  {treeKey} treeKey      트리 노드 키
     * @param  {boolean} replaceFlag 리다이렉트 여부 - replaceFlag true면 redirect 시켜 이전 히스토리를 대체한다.
     */
    routeByTreeKey(routeTreeKey, replaceFlag) {
      if (isEmpty(this.selectedNode) || routeTreeKey !== this.selectedNode.treeKey) {
        let menuUrl;
        let menuDepth;
        if (isEmpty(this.selectedMenu)) {
          menuUrl = this.$route.path.split('/')[1];
          if (isEmpty(this.rootMenuInfo)) {
            menuDepth = 1;
          } else {
            const index = findIndex(this.rootMenuInfo, (item) => item.url === menuUrl);
            menuDepth = index >= 0 ? this.rootMenuInfo[index].menuDepth : 1;
          }
        } else {
          menuUrl = this.selectedMenu.url;
          menuDepth = this.selectedMenu.menuDepth;
        }

        let path = `/${menuUrl}/${routeTreeKey}`;
        const params = {
          treeKey: routeTreeKey,
        };

        // 3depth 이상일 때 탭 파라미터 처리
        if (menuDepth >= 3 && !isNil(this.$route.params.activeTab)) {
          params.activeTab = this.$route.params.activeTab;
          path = `${path}/${this.$route.params.activeTab}`;
        }

        const location = {
          path: `${path}`,
          params,
        };
        if (replaceFlag === true) {
          this.$router.replace(location);
        } else {
          this.$router.push(location);
        }
      }
    },
    /**
     * 트리타입과 트리키를 가공하여 라우트를 위한 트리키를 생성하여 반환한다.
     * @author Ju
     * @param  {String} treeKey 트리키
     * @return {String}
     */
    concatTreeTypeAndTreeKey(treeKey) {
      return `${this.treeType}${TREE_TYPE_DELIMETER}${treeKey}`;
    },
    /**
     * 선택된 트리 노드로 스크롤을 이동시키는 Task를 생성한다.
     * el-scroll의 wrap element의 scroll height가 el-tree height에 맞게 계산이 완료되는 시점에
     * 선택된 트리 노드 위치로 스크롤이 이동되도록 한다.
     * @author Ju
     * @param  {Object} el 선택된 트리 노드 Element 객체
     */
    createScrollTask(el) {
      const offsetTop = el.offsetTop;
      const wrap = this.$refs[this.scrollRef].$refs.wrap;
      this.$taskManager.createTask(
        this.scrollTask,
        0.2,
        () => {
          if (wrap.scrollHeight >= offsetTop) {
            this.$taskManager.removeTask(this.scrollTask);
            wrap.scrollTop = offsetTop;
          }
        },
        false,
        10,
        () => {
          this.$taskManager.removeTask(this.scrollTask);
        },
      );
    },
    /* Dynmaic */
    /**
     * [동적] 라우트할 트리키를 포함하는 트리 노드를 로드한다.
     * @author Ju
     * @param  {String} routeTreeKey 라우트할 트리키
     */
    loadRouteTreeKeyNode(routeTreeKey) {
      this.routeTreeKey = routeTreeKey;
      this.loadedRouteNode = false;
      if (!this.loading) {
        if (!isNil(this.searchKeyword) && this.searchKeyword !== '') {
          // 검색된 결과를 보여주는 경우 root 기준으로 라우트 노드 기준으로 서브 트리를 조회하도록 한다.
          this.searchKeyword = null;
          this.reloadRootNode();
        } else {
          const treeKey = getTreeKeyExceptTreeType(routeTreeKey);
          const parentNode = this.getParentNodeByChildTreeKey(treeKey);
          if (!isNil(parentNode)) {
            if (parentNode.loaded) {
              this.reloadRootNode();
            } else {
              parentNode.expand();
            }
          }
        }
      }
    },
    /**
     * 트리키로 부모 노드를 찾아 반환한다.
     * @author Ju
     * @param  {String} treeKey 트리키
     * @return {Object} 부모 노드 객체
     */
    getParentNodeByChildTreeKey(treeKey) {
      const parentTreeKey = !isNil(treeKey) && treeKey.lastIndexOf(TREE_KEY_DELIMETER) > -1 ? treeKey.substring(0, treeKey.lastIndexOf(TREE_KEY_DELIMETER)) : null;
      if (!isNil(parentTreeKey)) {
        const node = this.$refs[this.treeRef].getNode(parentTreeKey);
        if (isNil(node)) {
          return this.getParentNodeByChildTreeKey(parentTreeKey);
        }
        return node;
      }
      return this.$refs[this.treeRef].root;
    },
    /**
     * [동적] 라우터 트리 데이터를 로드하기 위한 파라미터 객체를 반환한다.
     * @author Ju
     * @param  {[type]} node [description]
     * @return {[type]}      [description]
     */
    getRouterTreeParams(node) {
      const params = {};
      if (node.level === 0 && (isNil(this.searchKeyword) || this.searchKeyword === '')) {
        // 최초 로드 시 라우트된 트리 키 전달
        if (isNil(this.$route.params.treeKey)) {
          if (this.isDefaultTreeType()) {
            this.routeTreeKey = this.getDefaultRouteTreeKey();
          }
        } else {
          const routeTreeKey = this.$route.params.treeKey;
          const treeType = getTreeTypeFromTreeKey(routeTreeKey);
          if (isNil(treeType) && this.isDefaultTreeType()) {
            this.routeTreeKey = this.getDefaultRouteTreeKey();
            // 19.09.19 Ju 토폴로지맵에서 트리 타입 처리하고 나면 주석의 소스로 변경
            // this.routeTreeKey = this.concatTreeTypeAndTreeKey(treeKey);
          } else if (treeType === this.treeType) {
            this.routeTreeKey = routeTreeKey;
          }
        }
      }
      if (!isNil(this.routeTreeKey)) {
        params.initTreeKey = getTreeKeyExceptTreeType(this.routeTreeKey);
        if (this.firstLoaded === false && this.isLinkRoute === true && params.initTreeKey.indexOf(PREFIX_OBJ_KEY) >= 0) {
          // 최초 로드이고 링크를 통해 논리/물리 그룹 트리 기준으로 라우트된 경우 해당 장비 관련 트리 노드만 나오도록 파라미터 전달
          params.targetObjId = params.initTreeKey.substring(params.initTreeKey.lastIndexOf(PREFIX_OBJ_KEY) + 1);
        }
      }
      return params;
    },
    /**
     * 라우트할 노드 정보 로드 후 해당 노드가 선택되도록 처리한다.
     * 노드 객체가 생성된 후 호출되도록 nextTick에서 호출하도록 한다.
     * @author Ju
     * @return {[type]} [description]
     */
    callbackLoadRouteNode() {
      if (this.firstLoaded === false) {
        this.firstLoaded = true;
      }
      if (this.isLinkRoute === true) {
        this.$store.dispatch('setIsLinkRoute', false);
      }
      if (!isNil(this.routeTreeKey)) {
        const routeTreeKey = this.routeTreeKey;
        this.routeTreeKey = null;
        this.loadedRouteNode = true;
        this.changeRouteTreeKey(routeTreeKey);
      } else if (!isNil(this.searchKeyword) && this.searchKeyword !== '' && !isEmpty(this.selectedNode)) {
        // 검색된 결과에 기존에 선택된 대상이 있는 경우 선택되도록 함.
        const node = this.$refs[this.treeRef].getNode(this.selectedNode.treeKey);
        if (!isEmpty(node)) {
          this.$refs[this.treeRef].setCurrentKey(node.data.treeKey);
        }
      }
    },
    /* Override */
    /* eslint-disable no-unused-vars */
    /**
     * 유효한 노드인지 확인한다. (선택 가능한 노드)
     * @author Ju
     * @param  {Object}  node el-tree node 객체
     * @return {Boolean}
     */
    isValidNode(node) {
      return true;
    },
    /**
     * 기본적으로 rootNode의 treeKey를 반환한다.
     * 필요 시 override 한다.
     * @author Ju
     * @return {String} treeKey
     */
    getDefaultRouteTreeKey() {
      return this.concatTreeTypeAndTreeKey(rootNode.treeKey);
    },
    /**
     * TreeData 변경 감지 후 트리 라우터 처리 전 핸들러
     * @author Ju
     */
    handlerForChangeTreeDataBeforeRoute(treeData) {
      // treeData 변경 후 라우트 처리 전 수행할 로직
    },
  },
};



// WEBPACK FOOTER //
// ./src/mixins/router/tree.js